import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet, fnSave } from "@shared/api/restApi";
import {
  applySlip,
  CalAm,
  CalAmBulk,
  getInitFormData,
  getTablebySql,
} from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { showPopupModal } from "@widgets/CommonModal";
import SN_SAL_EST_REG_001_POP_CARD01 from "./SN_SAL_EST_REG_001_POP_CARD01";
import { dateToString } from "@shared/utils/DateUtils";
import { CellValueChangedEvent } from "@shared/components/GridV2/SnGridTypes";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { RowNode } from "ag-grid-community";
import {
  buildJsAddinfoFromFlags,
  extractAddInfoFlags,
  hydrateRowsWithAddInfo,
} from "@shared/utils/AddInfoUtils";
import { openAddInfoPopup } from "@widgets/commonpopup/sys/openAddInfoPopup";
import { openAddInfoRegPopup } from "@widgets/commonpopup/sys/openAddInfoRegPopup";
import { executeApprovalSubmit } from "@shared/utils/approval/approvalIntegration";
import { submitApproval } from "@shared/api/approvalSubmitApi";
import { SnButton } from "@shared/components/SnButton";

interface Props {
  Menuprops: menuInfo;
}

// 견적등록
const SN_SAL_EST_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo, config } = global();

  // 전자결재 연동 여부
  const isApprovalVisible = config?.MAS0121?.dc_ctrl === "Y";

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });

  const [selectedDocu, setSelectedDocu] = useState<string>("");
  const [selectedDtDocu, setSelectedDtDocu] = useState<string>("");
  // grid1 부가항목 저장 시 js_addinfo로 묶을 동적 컬럼 코드 목록이다.
  const [addInfoFlags, setAddInfoFlags] = useState<string[]>([]);

  // 부가항목 설정에 등록된 항목코드(cd_flag)를 조회해서 저장 시 js_addinfo 구성 기준으로 사용한다.
  const loadAddInfoFlags = async (idTarget = "grid1") => {
    try {
      const data: any = await fnGetDataSet({
        id: "sys_addinfo_pop_01_01",
        params: {
          cd_menu: Menuprops.cd_menu,
          id_target: idTarget,
        },
      });
      const flags = extractAddInfoFlags(data?.[0] || []);
      setAddInfoFlags(flags);
      return flags;
    } catch (error: any) {
      setAddInfoFlags([]);
      return [];
    }
  };

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData);
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
        await loadAddInfoFlags("grid1");
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
    onPrint,
  }));

  const onSearch = async () => {
    const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
    const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirm",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }
    }

    try {
      const params = {
        id: "sn_sal_est_reg_001_01",
        params: searchRef.current.getFormdata(),
      };

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);

      // 조회된 sal_esth.js_addinfo 값을 grid1 동적 컬럼 값으로 펼쳐서 기존 입력값을 화면에 표시한다.
      const flags =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid1");
      setDataSources({
        grid1: hydrateRowsWithAddInfo(data[0] || [], flags),
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRowH = (): any => {
    try {
      const salEstChanges =
        gridRefs.current["grid1"].current.getChange().insert;
      if (salEstChanges.length > 0) return false;

      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        cd_biz: userInfo.cd_biz,
        no_est: "",
        cd_dept: userInfo.cd_dept,
        cd_gp: userInfo.cd_gp,
        cd_emp: userInfo.cd_emp,
        nm_emp: userInfo.nm_emp,
        dt_est: today,
        dt_dlv: today,
        dt_valid: today,
        yn_memo: "N",
        cd_cust: "",
        nm_cust: "",
        yn_cust: "N",
        cd_exch: "000",
        rt_exch: 1,
        fg_trns: "1",
        tp_um: "001",
        st_proc: "P",
        fg_est: "001",
        no_hst: 1,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        dy_pay: 0,
        fg_bill: "",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onAddRow = () => {
    try {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH && focusedNodeH.data?.st_proc !== "P") {
        showDialog("alter", "진행된 건입니다.");
        return;
      }
      gridRefs.current["grid2"].current?.addRow();
    } catch (error: any) {}
  };

  const onBeforeAddRowL = (): any => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      if (focusedNodeH == null) {
        showDialog("alter", "선택된 행이 없습니다.");
        return false;
      }

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      const fg_trns = focusedNodeH.data?.fg_trns ?? "1";
      let fg_vat = "11";
      if (fg_trns === "1") {
        fg_vat = "11";
      } else if (fg_trns === "2" || fg_trns === "3") {
        fg_vat = "14";
      } else if (fg_trns === "4" || fg_trns === "5") {
        fg_vat = "15";
      }
      const maxNoLine =
        gridRefs.current["grid2"].current.getMaxValue("no_line");
      const today = dateToString(new Date());
      return {
        cd_firm: userInfo.cd_firm,
        no_est: focusedNodeH.data?.no_est,
        no_hst: focusedNodeH.data?.no_hst,
        fg_trns: fg_trns,
        fg_vat: fg_vat,
        st_proc: focusedNodeH.data?.st_proc,
        yn_item: "N",
        no_seq: 0,
        dt_dlv: today,
        no_line: maxNoLine + 1,
        qt: 0,
        um: 0,
        cd_item: "",
        nm_item: "",
        dc_item_spec: "",
        dc_item_unit: "",
      };
    } catch (error: any) {
      return false;
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");
    if (!confirm) return;

    gridRefs.current["grid2"].current?.deleteAllRows();
    gridRefs.current["grid1"].current?.deleteSelectedRows();

    const realDeletedRows =
      gridRefs.current["grid1"].current.getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return;
    }

    return await onSave();
  };

  const onDeleteRow = async () => {
    if (gridRefs.current["grid2"].current?.getDisplayedRowCount() < 1) return;
    gridRefs.current["grid2"].current?.deleteSelectedRows();
    setFooterDataToRow();
  };

  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const requiredValuesCheckH =
        gridRefs.current["grid1"].current.requiredValuesCheck();
      if (!requiredValuesCheckH) return false;

      const requiredValuesCheckL =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheckL) return false;

      const salEstHChanges = gridRefs.current["grid1"].current.getChange();
      const salEstLChanges = gridRefs.current["grid2"].current.getChange();
      const headerInserts = salEstHChanges.insert ?? [];
      const lineInserts = salEstLChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        salEstHChanges.modifiedRowcount === 0 &&
        salEstLChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      if (headerInserts.length > 0) {
        const focusedRowNodeH =
          gridRefs.current["grid1"].current.focusedRowNode();
        const noSlip = await applySlip({
          cd_column: "no_est",
          cd_module: "SAL",
          no_line: 6,
          cd_prefix: "EST",
          dt: focusedRowNodeH.data.dt_est,
          girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
          inserts: [salEstHChanges, salEstLChanges],
          cd_menu: Menuprops.cd_menu,
        });
        gridRefs.current["grid2"].current.applyFilters({ no_est: noSlip });
      }

      // 저장 직전에 grid1 동적 부가항목 값을 sal_esth.js_addinfo로 묶는다.
      // insert/update payload 구조는 유지하고 row 내부에 js_addinfo만 추가한다.
      const flagsForSave =
        addInfoFlags.length > 0
          ? addInfoFlags
          : await loadAddInfoFlags("grid1");
      const salEstHWithAddInfo = {
        ...salEstHChanges,
        insert: (salEstHChanges.insert ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
        update: (salEstHChanges.update ?? []).map((row: any) => ({
          ...row,
          js_addinfo: buildJsAddinfoFromFlags(row, flagsForSave),
        })),
      };

      const saveData = {
        sal_esth: salEstHWithAddInfo,
        sal_estl: salEstLChanges,
      };

      const ok = await fnSave("SN_SAL_EST_REG_001_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onGrid2BtnsClick = async (id: string) => {
    if (id == "addRow") {
      onAddRow();
    }
    if (id == "deleteRow") {
      onDeleteRow();
    }
    if (id === "aButton_nameCard") {
      // 담당자 관리
      const focusedRowNodeH =
        gridRefs.current["grid1"].current.focusedRowNode();
      const selectedData = {
        cd_source: focusedRowNodeH.data.cd_cust,
        nm_cust: focusedRowNodeH.data.nm_cust,
      };
      const component = (
        <SN_SAL_EST_REG_001_POP_CARD01 selectedData={selectedData} />
      );
      const result = await showPopupModal({
        title: "담당자 도움",
        children: component,
        width: "55%",
      });
      if (!result || result === "close") return;
      const headerResult = result[0];
      if (focusedRowNodeH && headerResult[0]) {
        focusedRowNodeH.setDataValue("nm_cust_emp", headerResult[0].nm_person);
        focusedRowNodeH.setDataValue("dc_cust_tel", headerResult[0].no_tel);
        focusedRowNodeH.setDataValue("dc_cust_hp", headerResult[0].no_hp);
        focusedRowNodeH.setDataValue("dc_cust_mail", headerResult[0].dc_email);
      }
    }
  };

  const onFocusedRowChanged = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_est = selectedRow.no_est;
      const dt_est = selectedRow.dt_est || "";
      setSelectedDocu(no_est);
      setSelectedDtDocu(dt_est);
    } else {
      return;
    }
  };

  const onCellValueChangedH = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    if (colId === "fg_trns") {
      const fg_trns = rowData.fg_trns ?? "1";
      let fg_vat = "11";
      if (fg_trns === "1") {
        fg_vat = "11";
      } else if (fg_trns === "2" || fg_trns === "3") {
        fg_vat = "14";
      } else if (fg_trns === "4" || fg_trns === "5") {
        fg_vat = "15";
      }
      rowNode.setDataValue("fg_vat", fg_vat);
    }
    if (colId === "cd_cust") {
      if (rowData.cd_cust) {
        const sqlQuery = {
          sql: "select count(1) as cnt from mas_cust where cd_firm = :cd_firm and cd_cust = :cd_cust",
          cd_firm: userInfo.cd_firm,
          cd_cust: rowData.cd_cust,
        };
        const masCustData = await getTablebySql(sqlQuery);
        if (masCustData && masCustData.length > 0) {
          rowNode.setDataValue("yn_cust", "Y");
          rowNode.setDataValue("nm_emp_cust1", masCustData[0].nm_emp_cust1);
        } else {
          rowNode.setDataValue("yn_cust", "N");
        }
      } else {
        rowNode.setDataValue("yn_cust", "N");
        rowNode.setDataValue("nm_emp_cust1", "");
      }
    }
    if (colId === "no_est") {
      if (!rowData.no_est) return;
      const sqlQuery = {
        sql: "select count(1) as cnt from sal_esth where cd_firm = :cd_firm and no_est = :no_est",
        cd_firm: userInfo.cd_firm,
        no_est: rowData.no_est,
      };
      const data = await getTablebySql(sqlQuery);
      if (data && data.length > 0) {
        if (data[0].cnt > 0) {
          await showDialog("alter", "견적번호호가 중복 되었습니다.");
          rowNode.setDataValue("no_est", "");
        }
      }
    }
    if (colId === "um_std_est") {
      const um_std_est = rowData.um_std_est ?? 0;
      const rt_matgin = rowData.rt_matgin ?? 0;
      const rt_extra = rowData.rt_extra ?? 0;

      const um =
        um_std_est -
        (um_std_est * rt_matgin) / 100 +
        (um_std_est * rt_extra) / 100;
      rowNode.setDataValue("um", um);
    }
    CalAmBulk(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const onCellValueChangedL = async (event: CellValueChangedEvent) => {
    if (!event) return;
    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const focusedRowNodeH = gridRefs.current["grid1"].current.focusedRowNode();

    if (colId === "cd_item") {
      if (rowData.cd_item) {
        const sqlQuery = {
          sql: "select * from mas_item where cd_firm = :cd_firm and cd_item = :cd_item",
          cd_firm: userInfo.cd_firm,
          cd_item: rowData.cd_item,
        };
        const masItemData = await getTablebySql(sqlQuery);
        if (masItemData && masItemData.length > 0) {
          rowNode.setDataValue("yn_item", "Y");
        } else {
          rowNode.setDataValue("yn_item", "N");
        }
      } else {
        rowNode.setDataValue("yn_item", "N");
      }
    }
    await CalAm(gridRefs.current["grid1"], gridRefs.current["grid2"], colId);
  };

  const setFooterDataToRow = () => {
    const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
    const footerData = gridRefs.current["grid2"].current.getFooterData();
    if (focusedNodeH) {
      focusedNodeH.setDataValue("am", footerData.am);
      focusedNodeH.setDataValue("am_k", footerData.am_k);
      focusedNodeH.setDataValue("am_vat", footerData.am_vat);
      focusedNodeH.setDataValue("am_supply", footerData.am_supply);
    }
  };

  const onAfterPopupClose = (event: any) => {
    const applyNodes = event.nodes;
    const colId = event.colId;
    if (colId === "cd_item") {
      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const fg_trns = focusedNodeH.data.fg_trns;
      let fg_vat = "11";
      if (fg_trns === "2" || fg_trns === "3") {
        fg_vat = "14";
      } else if (fg_trns === "4" || fg_trns === "5") {
        fg_vat = "15";
      }
      applyNodes.forEach((node: RowNode) => {
        node.setDataValue("fg_vat", fg_vat);
      });
    }
  };

  // grid1 헤더 우클릭의 부가항목 설정 팝업을 호출하고, 저장 후 컬럼/항목코드를 다시 읽는다.
  const onOpenAddInfoPopup = async (idTarget = "grid1") => {
    // 현재 grid1 컬럼 설정을 넘겨 SYS_ADDINFO_POP_01에서 상단표시항목/부가항목 설정 대상으로 사용한다.
    const saved = await openAddInfoPopup({
      cd_menu: Menuprops.cd_menu!,
      id_target: idTarget,
      columnInfos: columns.grid1 || [],
    });
    if (!saved) return;

    const rtn: any = await getInitFormData();
    setFormControlData(rtn.formControlData);
    setFormData((prev: any) => ({
      ...(rtn.formData || {}),
      ...(prev || {}),
    }));
    setColums({
      grid1: rtn.grid.grid1,
      grid2: rtn.grid.grid2,
    });

    // 부가항목 설정이 바뀌었을 수 있으므로 컬럼 설정과 flag 목록을 다시 동기화한다.
    await loadAddInfoFlags(idTarget);
    await onSearch();
  };

  // grid1 헤더 우클릭 추가 메뉴를 처리한다.
  const onGrid1ExtraHeaderMenuClick = async (event: {
    id: string;
    params: any;
  }) => {
    if (event.id === "extra_item_setting") {
      await onOpenAddInfoPopup("grid1");
    }
  };

  // grid1 로우 우클릭 메뉴에서 부가항목 등록/수정 팝업을 호출한다.
  const handleContextMenu = (params: any) => {
    switch (params?.id) {
      case "wf_sn_sal_est_info":
        (async () => {
          // 부가항목 등록/수정은 현재 선택된 견적 헤더 row를 기준으로 처리한다.
          const rowNode = gridRefs.current["grid1"].current.focusedRowNode();
          if (!rowNode) {
            await showDialog("alter", "선택된 행이 없습니다.");
            return;
          }
          if (!Menuprops.cd_menu) {
            await showDialog("alter", "메뉴 코드가 없습니다.");
            return;
          }

          const result = await openAddInfoRegPopup({
            title: "견적부가항목",
            cd_menu: Menuprops.cd_menu,
            id_target: "grid1",
            rowData: { ...rowNode.data },
            width: "980px",
            height: "760px",
            rememberSizeKey: "SN_SAL_EST_REG_001",
          });
          if (!result || result === "close") return;

          // 팝업에서 반환한 값은 grid1 row에 반영하고, 저장 시 js_addinfo로 다시 묶는다.
          const values = result?.values ?? {};
          Object.keys(values).forEach((key) => {
            rowNode.setDataValue(key, values[key]);
          });
        })();
        break;
      default:
        break;
    }
  };

  const onPrint = async () => {
    gridRefs.current["grid1"].current?.stopEditing();
    gridRefs.current["grid2"].current?.stopEditing();

    const searchInfo = searchRef.current.getFormdata();
    const selectedRow = gridRefs.current["grid1"].current?.getSelectedRows();
    if (selectedRow.length < 1) {
      await showDialog("alter", "선택된 견적정보가 없습니다.");
      return;
    }
    return {
      cd_firm: userInfo.cd_firm,
      cd_biz: searchInfo.cd_biz,
      no_est: selectedRow[0].no_est,
    };
  };

  const onGrid1BtnsClick = async (id: string) => {
    if (id == "aButton_Approve") {
      gridRefs.current["grid1"].current?.stopEditing();
      gridRefs.current["grid2"].current?.stopEditing();

      const focusedNodeH = gridRefs.current["grid1"].current.focusedRowNode();
      const modifiedRowsH = gridRefs.current["grid1"].current.getChange();
      const modifiedRowsL = gridRefs.current["grid2"].current.getChange();
      const hasModified =
        modifiedRowsH.modifiedRowcount > 0 ||
        modifiedRowsL.modifiedRowcount > 0;
      if (hasModified) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장 후 결재상신 하시겠습니까?"
        );
        if (!confirm) return;
        const isSaved: any = await onSave();
        if (!isSaved) return;
      }

      if (!focusedNodeH?.data?.no_est) {
        await showDialog("alter", "결재상신할 견적건을 선택해 주세요.");
        return;
      }

      if (focusedNodeH.data.st_proc !== "P") {
        await showDialog("alter", "의뢰 상태인 건만 결재상신할 수 있습니다.");
        return;
      }

      await executeApprovalSubmit({
        menuCode: "SN_SAL_EST_REG_001",
        noRef: focusedNodeH.data.no_est,
        userInfo: {
          cd_firm: userInfo.cd_firm,
          cd_biz: userInfo.cd_biz,
          cd_dept: userInfo.cd_dept,
          cd_emp: userInfo.cd_emp,
          cd_user: userInfo.cd_user,
        },
        submitApproval,
        showDialog,
        openWindow: (url) => window.open(url),
        noUrlMessage: "결재창 URL을 수신하지 못했습니다.",
      });
      return;
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onFocusedRowChanged,
          onCellValueChanged: onCellValueChangedH,
          onContextMenu: handleContextMenu,
          onExtraHeaderMenuClick: onGrid1ExtraHeaderMenuClick,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onBtnAddClick: onGrid2BtnsClick,
          onBtnDeleteClick: onGrid2BtnsClick,
          onAfterPopupClose: onAfterPopupClose,
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "견적정보",
              // 헤더 우클릭 메뉴에 부가항목 설정을 추가한다.
              extraHeaderMenuItems: [
                { id: "extra_item_setting", name: "부가항목 설정" },
              ],
            }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_est"] }}
            header={
              <div>
                {isApprovalVisible && (
                  <SnButton
                    text={"결재상신"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Approve");
                    }}
                  />
                )}
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]}
            columnConfig={grids.grid2.columns}
            rowData={grids.grid2.dataSource}
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
            // header={
            //   <div className="flex flex-row" style={{ width: 380 }}>
            //     <div style={{ width: 125 }}>
            //       <SnButton
            //         text={"담당자관리"}
            //         onClick={() => {
            //           onGrid2BtnsClick("aButton_nameCard");
            //         }}
            //       />
            //     </div>
            //   </div>
            // }
          />
        </SnResizeBar>
        <SnFileUploadBox
          selectedDocu={selectedDocu}
          selectedLineDocu={0}
          selectedDtDocu={selectedDtDocu}
        />
      </div>
    </div>
  );
  //#endregion
});

export default SN_SAL_EST_REG_001;
